import PropTypes from 'prop-types'
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, VStack, Text, Box, Divider, Badge, Grid, GridItem } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { getProjectExtraDetailAuditTrails } from 'api'
import { format as formatDate } from 'date-fns'

const formatFieldName = (fieldName) => {
  // Convert camelCase to Title Case
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return <Text as="span" fontStyle="italic" color="gray.500">(empty)</Text>
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return String(value)
}

export const ModalProjectDetailOverviewHistory = ({ isOpen, onClose, projectExtraDetailId }) => {
  const [auditTrails, setAuditTrails] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const loadAuditTrails = async () => {
    if (!projectExtraDetailId) return

    setIsLoading(true)
    try {
      const data = await getProjectExtraDetailAuditTrails(projectExtraDetailId)
      setAuditTrails(data)
    } catch (err) {
      setAuditTrails([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && projectExtraDetailId) {
      loadAuditTrails()
    }
  }, [isOpen, projectExtraDetailId])

  const getUserName = (trail) => {
    if (trail.firstname && trail.lastname) {
      return `${trail.firstname} ${trail.lastname}`
    }
    if (trail.email) {
      return trail.email
    }
    return 'Unknown User'
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Overview Change History</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="600px" overflow="auto" pb={6}>
          {isLoading ? (
            <Text>Loading...</Text>
          ) : auditTrails.length === 0 ? (
            <Text color="gray.500">No change history available.</Text>
          ) : (
            <VStack alignItems="stretch" gap={4}>
              {auditTrails.map((trail, index) => (
                <Box key={trail.id}>
                  <Box mb={3}>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatDate(new Date(trail.createdAt), 'MM/dd/yyyy hh:mm:ss a')}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Changed by {getUserName(trail)} • {trail.changedFields?.length || 0} field{trail.changedFields?.length !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                  <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={3}>
                    {trail.changedFields?.map((field) => (
                      <GridItem key={field}>
                        <Box borderWidth="1px" borderColor="gray.300" borderRadius="md" p={3} bg="gray.50">
                          <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
                            {formatFieldName(field)}
                          </Text>
                          <VStack alignItems="stretch" gap={2}>
                            <Box>
                              <Badge colorScheme="red" fontSize="xs" mr={2}>Old:</Badge>
                              <Text as="span" fontSize="sm" display="inline-block" mt={1} ml={1}>
                                {formatFieldValue(trail.oldData?.[field])}
                              </Text>
                            </Box>
                            <Box>
                              <Badge colorScheme="green" fontSize="xs" mr={2}>New:</Badge>
                              <Text as="span" fontSize="sm" display="inline-block" mt={1} ml={1}>
                                {formatFieldValue(trail.newData?.[field])}
                              </Text>
                            </Box>
                          </VStack>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                  {index < auditTrails.length - 1 && <Divider mt={4} />}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

ModalProjectDetailOverviewHistory.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  projectExtraDetailId: PropTypes.number
}
